<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  ParameterCombinationDto,
  TrackItMeasurementFilterForCombinationDto,
  TrackItMeasurementParameterFilterDto,
  TrackItMeasurementPropertyFilterDto,
} from '@/api/generator';
import {
  filterLabelParts,
  deriveValueBearingCounts,
  trackItFilterIdentityKey,
  trackItFilterName,
  trackItPropertyIdentityKey,
  translateMeasurementPropertyName,
} from './trackItFilters';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import FormAddButton from './FormAddButton.vue';
import SelectTrackItFiltersDialog from './SelectTrackItFiltersDialog.vue';
import { I18nKey } from '@/utils/typings';
import { trackItLogo } from '@/components/icons/icons';
import { useFormMode } from '@/components/forms/useFormMode.ts';

const mode = useFormMode();

const props = defineProps<{
  combinations: ParameterCombinationDto[] | undefined | null;
  // Single source of truth for the picker (see useTrackItFilters.ts); the dialog derives its
  // views from this list.
  trackItFilterCatalog: TrackItMeasurementFilterForCombinationDto[];
  // One representative filter per entity actually present in the combinations (see
  // selectedTrackItFilters), not the suggestion endpoint's isEnabled echo.
  filtersFromCombinations: TrackItMeasurementParameterFilterDto[];
  propertiesFromCombinations: TrackItMeasurementPropertyFilterDto[];

  loadTrackItFilterSuggestions: () => Promise<void>;
}>();

const emit = defineEmits<{
  apply: [
    selectedFilters: TrackItMeasurementParameterFilterDto[],
    selectedProperties: TrackItMeasurementPropertyFilterDto[],
  ];
  requestParameterSuggestions: [stagedFilters: TrackItMeasurementParameterFilterDto[]];
}>();

const valueBearingCounts = computed(() => deriveValueBearingCounts(props.combinations, false));
const valueBearingPropertyCounts = computed(() =>
  deriveValueBearingCounts(props.combinations, true),
);

const dialogOpen = ref(false);
const loadingSuggestions = ref(false);

async function openDialog() {
  loadingSuggestions.value = true;
  await props.loadTrackItFilterSuggestions();
  loadingSuggestions.value = false;
  dialogOpen.value = true;
}
</script>

<template>
  <v-card class="container-level-2">
    <v-card-item>
      <v-card-title class="d-flex align-center">
        <v-icon color="on-off-white" :size="24" :icon="trackItLogo" class="mr-3" />
        <h5>{{ $t('TrackItFilters' as I18nKey) }}</h5>
        <VSpacer />
        <FormAddButton
          v-if="mode !== 'details'"
          :label="'SelectTrackItFilters' as I18nKey"
          :loading="loadingSuggestions"
          @click="openDialog"
        />
      </v-card-title>
    </v-card-item>

    <v-card-text>
      <p
        v-if="!filtersFromCombinations.length && !propertiesFromCombinations.length"
        class="text-body-1 text-primary-lighten-2 font-weight-semibold text-pre-line"
      >
        {{ $t('NoTrackItFiltersSelected' as I18nKey) }}
      </p>

      <v-row v-else>
        <v-col v-if="filtersFromCombinations.length">
          <h6 class="text-primary">
            {{ $t('TrackItMeasurementParameter_other' as I18nKey) }}
          </h6>
          <div class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="filter in filtersFromCombinations"
              :key="trackItFilterIdentityKey(filter)"
              size="small"
              class="text-body-2"
            >
              <IdentifierLabel v-bind="filterLabelParts(filter)" />
            </v-chip>
          </div>
        </v-col>

        <v-col v-if="propertiesFromCombinations.length">
          <h6 class="text-primary">
            {{ $t('TrackItMeasuringProperty_other' as I18nKey) }}
          </h6>
          <div class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="filter in propertiesFromCombinations"
              :key="trackItPropertyIdentityKey(trackItFilterName(filter) ?? '')"
              size="small"
              class="text-body-2"
            >
              {{ translateMeasurementPropertyName(trackItFilterName(filter)) }}
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <SelectTrackItFiltersDialog
      v-model="dialogOpen"
      :filtersFromCombinations="filtersFromCombinations"
      :propertiesFromCombinations="propertiesFromCombinations"
      :trackItFilterCatalog="trackItFilterCatalog"
      :valueBearingCounts="valueBearingCounts"
      :valueBearingPropertyCounts="valueBearingPropertyCounts"
      @apply="
        (appliedFilters, appliedProperties) => emit('apply', appliedFilters, appliedProperties)
      "
      @requestParameterSuggestions="
        (stagedFilters) => emit('requestParameterSuggestions', stagedFilters)
      "
    />
  </v-card>
</template>
